'use client';
import { FunctionComponent, useState, useRef } from 'react';
import { GalleryImage } from '@xFrame4/business/images/ImageGallery';
import SmoothImage from '@xFrame4/components/common/SmoothImage';

export interface ImageGallerySliderProps
{
    /** The gallery images to display. */
    galleryImages: GalleryImage[];
    /** The class name to add to the container. */
    className?: string;
}

/**
 * Component: an image slider with navigation arrows for browsing through gallery images.
 */
const ImageGallerySlider: FunctionComponent<ImageGallerySliderProps> = (props) =>
{
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);

    /** Handle previous image */
    const handlePrevious = () =>
    {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? props.galleryImages.length - 1 : prevIndex - 1
        );
    };

    /** Handle next image */
    const handleNext = () =>
    {
        setCurrentIndex((prevIndex) =>
            prevIndex === props.galleryImages.length - 1 ? 0 : prevIndex + 1
        );
    };

    /** Handle touch start */
    const handleTouchStart = (e: React.TouchEvent) =>
    {
        touchStartX.current = e.touches[0].clientX;
    };

    /** Handle touch move */
    const handleTouchMove = (e: React.TouchEvent) =>
    {
        touchEndX.current = e.touches[0].clientX;
    };

    /** Handle touch end */
    const handleTouchEnd = () =>
    {
        if (!touchStartX.current || !touchEndX.current) return;

        const distance = touchStartX.current - touchEndX.current;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && props.galleryImages.length > 1)
        {
            handleNext();
        }
        if (isRightSwipe && props.galleryImages.length > 1)
        {
            handlePrevious();
        }

        // Reset values
        touchStartX.current = 0;
        touchEndX.current = 0;
    };


    /** Navigation arrows */
    const cmpNavigationArrows = props.galleryImages.length > 1 ? (
        <>
            <button
                className="slider-nav-button slider-nav-prev"
                onClick={handlePrevious}
                aria-label="Previous image"
            >
                <i className="fas fa-chevron-left"></i>
            </button>
            <button
                className="slider-nav-button slider-nav-next"
                onClick={handleNext}
                aria-label="Next image"
            >
                <i className="fas fa-chevron-right"></i>
            </button>
        </>
    ) : null;

    /** Render */
    return (
        <div className={`image-gallery-slider ${props.className ?? ''}`}>
            {props.galleryImages.length > 0 && (
                <div 
                    className="slider-container position-relative"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="slider-viewport">
                        <div 
                            className="slider-track"
                            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                        >
                            {props.galleryImages.map((image, index) => (
                                <div key={index} className="slider-slide">
                                    <SmoothImage
                                        src={image.veryLarge169.url}
                                        width={image.veryLarge169.width}
                                        height={image.veryLarge169.height}
                                        className="slider-image w-100"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    {cmpNavigationArrows}
                </div>
            )}
        </div>
    );
}

export default ImageGallerySlider;